import { Link } from 'react-router-dom';
import { postListFetcher } from '@/shared/api/list';
import { formatCost, formatUnixDateTime } from '@/shared/lib/datetime';
import { GenericListPage } from '@/shared/ui/list/GenericListPage';
import type { ListPageConfig, ListResponse, ListRow } from '@/shared/ui/list/types';

interface PartsCart extends ListRow {
  id: number;
  user_role?: string;
  update_time?: number;
  user?: string;
  user_id?: number;
  phone?: string;
  part_count?: number;
  item_qty?: number;
  client_sum?: number;
  distributor_sum?: number;
}

interface CartsData {
  carts?: PartsCart[];
  total_cart_count?: number;
  total_distributor_sum?: number;
  total_client_sum?: number;
}

const ROLE_OPTIONS = [
  { value: 'service', label: 'СТО' },
  { value: 'customer_individual', label: 'Клиент' },
  { value: 'autopark', label: 'Автопарк' },
];

const ROLE_NAMES: Record<string, string> = {
  customer_individual: 'Клиент',
  service: 'Сервис',
};

const getCartsData = (data: ListResponse) => (data.data ?? {}) as CartsData;

const TotalItem = ({ title, value }: { title: string; value: string | number }) => (
  <div className="col-md-4">
    <strong>{title}:</strong> {value}
  </div>
);

const config: ListPageConfig<PartsCart> = {
  queryKey: 'parts-carts',
  title: 'Корзины',
  errorText: 'Ошибка загрузки корзин',
  fetchList: postListFetcher('/part/cart/list/'),
  rows: (data) => getCartsData(data).carts ?? [],
  filters: [
    { name: 'cart_updated_from', label: 'Начиная с даты', kind: 'datetime' },
    { name: 'cart_updated_to', label: 'Заканчивая датой', kind: 'datetime' },
    { name: 'role', label: 'Роль', kind: 'select', options: ROLE_OPTIONS },
    { name: 'customer_phone', label: 'Телефон', kind: 'text', placeholder: 'Телефон' },
  ],
  summary: (data) => {
    const totals = getCartsData(data);
    return (
      <div className="row" style={{ marginBottom: 16 }}>
        <TotalItem title="Всего корзин" value={totals.total_cart_count ?? 0} />
        <TotalItem
          title="Сумма от поставщика"
          value={`${formatCost(totals.total_distributor_sum)} ₽`}
        />
        <TotalItem title="Сумма для клиента" value={`${formatCost(totals.total_client_sum)} ₽`} />
      </div>
    );
  },
  columns: [
    {
      header: 'ID корзины',
      render: (cart) => <Link to={`/parts/carts/${cart.id}`}>{cart.id}</Link>,
    },
    { header: 'Роль', render: (cart) => ROLE_NAMES[String(cart.user_role)] },
    { header: 'Дата обновления корзины', render: (cart) => formatUnixDateTime(cart.update_time) },
    { header: 'Заказчик', render: (cart) => cart.user },
    {
      header: 'ID заказчика',
      render: (cart) => <Link to={`/users/user/${cart.user_id}`}>{cart.user_id}</Link>,
    },
    { header: 'Телефон', render: (cart) => `+${cart.phone}` },
    { header: 'Кол-во позиций', render: (cart) => cart.part_count },
    { header: 'Количество деталей', render: (cart) => `${cart.item_qty} шт` },
    { header: 'Сумма для клиента', render: (cart) => `${formatCost(cart.client_sum)} ₽` },
    { header: 'Сумма от поставщика', render: (cart) => `${formatCost(cart.distributor_sum)} ₽` },
  ],
};

export const PartsCartsPage = () => <GenericListPage config={config} />;
